import { createSelector } from '@reduxjs/toolkit';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import { UserLoop } from 'types';
import type { RootState } from '..';
import { Effects, ExtraFormulaFields, RemixerHistoryItem } from './types';
import initialState from './initialState';

export const selectProject = createSelector(
  [(state: RootState) => state.remixer.project],
  (project) => project
);

export const selectEffectsState = createSelector(
  [selectProject],
  ({ effects }) => effects
);

export const selectEffectValue = (effect: Effects) =>
  createSelector([selectEffectsState], (effects) => effects[effect]);

export const selectInitialSong = createSelector(
  [
    (state: RootState) => state.remixer.initialSongRequestId,
    (state: RootState) => state.remixer.songs,
  ],
  (initialSongRequestId, songs) => {
    if (!initialSongRequestId) {
      return null;
    }
    return songs[initialSongRequestId];
  }
);

export const checkUndoDisabled = createSelector(
  [selectProject],
  ({ historyPointer }) => historyPointer === initialState.project.historyPointer
);

export const selectHistory = createSelector(
  [selectProject],
  ({ history }) => history
);

export const selectHistoryPointer = createSelector(
  [selectProject],
  ({ historyPointer }) => historyPointer
);

export const checkRedoDisabled = createSelector(
  [selectHistoryPointer, selectHistory],
  (historyPointer, { length }) => historyPointer === length - 1
);

export const selectCurrentHistoryItem = createSelector(
  [selectHistory, selectHistoryPointer],
  (history, historyPointer): RemixerHistoryItem | null =>
    history[historyPointer] || null
);

export const selectSongs = createSelector(
  [(state: RootState) => state.remixer.songs],
  (songs) => songs
);

export const selectCurrentSong = createSelector(
  [selectInitialSong, selectCurrentHistoryItem, selectSongs],
  (initialSong, historyItem, songs) => {
    if (historyItem) {
      const { songRequestId } = historyItem;
      return songs[songRequestId];
    }
    return initialSong;
  }
);

export const checkCurrentSongLoading = createSelector(
  [selectCurrentSong],
  (currentSong) => (currentSong ? currentSong.loading : true)
);

export const checkCurrentSongError = createSelector(
  [selectCurrentSong],
  (currentSong) => (currentSong ? currentSong.error : null)
);

export const checkProjectHasChanges = createSelector(
  [selectProject, (state: RootState) => state.remixer.prevProjectState],
  (project, prevProjectState) => {
    const propsToCompare = ['history', 'effects', 'historyPointer', 'title'];
    return !isEqual(
      pick(project, propsToCompare),
      pick(prevProjectState, propsToCompare)
    );
  }
);

export const selectCurrentProjectId = createSelector(
  [selectProject],
  ({ id }) => id
);

export const selectProjectTitle = createSelector(
  [selectProject],
  ({ title }) => title
);

export const selectLastSavedAt = createSelector(
  [selectProject],
  ({ lastSavedAt }) => lastSavedAt
);

export const selectEffectsOrder = createSelector(
  [selectProject],
  ({ effectsOrder }) => effectsOrder
);

export const selectUserLoop = createSelector(
  [selectHistory, selectHistoryPointer, selectInitialSong],
  (remixerHistory, historyPointer, initialSong) => {
    for (let i = historyPointer; i >= 0; i -= 1) {
      const {
        formula: { field, value },
      } = remixerHistory[i];

      if (field === ExtraFormulaFields.RemoveUserLoop) {
        return null;
      }

      if (field === ExtraFormulaFields.UserLoop) {
        return value as UserLoop;
      }
    }

    const initialSongData = initialSong?.data;
    if (initialSongData && 'userLoop' in initialSongData) {
      return initialSongData.userLoop;
    }

    return null;
  }
);
